import React, { useState } from 'react';
import { TimeSheet } from "./Dashboard";
import ConfirmPopup from '../../Components/Popups/ConfirmPopup';
import FeedbackPopup from '../../Components/Popups/FeedbackPopup';
import RejectPopup from '../../Components/Popups/RejectPopup';
import { confirmBooking } from '../../api/bookings';
import { useLoader } from '../../hooks/LoaderContext';
import { format } from 'date-fns';

export const TimeSheetTable = ({ data, onUpdate }: { data: TimeSheet[], onUpdate: () => void }) => {
  const { setIsLoading } = useLoader();
  const [isConfirmPopupOpen, setIsConfirmPopupOpen] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [isFeedbackPopupOpen, setIsFeedbackPopupOpen] = useState(false);
  const [isRejectPopupOpen, setIsRejectPopupOpen] = useState(false);
  const [selectedBookingId, setSelectedBookingId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sortConfig, setSortConfig] = useState<{
    key: keyof TimeSheet;
    direction: 'ascending' | 'descending';
  } | null>(null);

  const sortedData = React.useMemo(() => {
    if (!sortConfig) return data;

    return [...data].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];
      
      if (aValue === null || bValue === null) return 0;
      
      if (aValue < bValue) {
        return sortConfig.direction === 'ascending' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'ascending' ? 1 : -1;
      }
      return 0;
    });
  }, [data, sortConfig]);

  const requestSort = (key: keyof TimeSheet) => {
    let direction: 'ascending' | 'descending' = 'ascending';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const handleRowClick = (bookingId: number) => {
    setSelectedBookingId(bookingId);
    setIsConfirmPopupOpen(true);
  };

  const handleConfirmSuccess = async (data: { PONumber: string }) => {
    if (!selectedBookingId) return;
    
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await confirmBooking(selectedBookingId.toString(), {
        PONumber: data.PONumber
      });
      
      if (response) {
        setIsConfirmPopupOpen(false);
        setIsFeedbackPopupOpen(true);
        onUpdate();
      }
    } catch (error) {
      setError('Failed to confirm booking. Please try again.');
      console.error('Error confirming booking:', error);
    } finally {
      setIsLoading(false);        
     setIsConfirming(false);
    }
  };

  const closeFeedbackPopup = () => {
    setIsFeedbackPopupOpen(false);
  };

  const handleReject = async (reason: string) => {
    onUpdate();
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return format(date, 'dd/MM/yyyy');
    } catch {
      return dateString;
    }
  };

  const selectedTimeSheet = sortedData.find(sheet => sheet.bookingId === selectedBookingId);

  return (
    <div className="timesheet-table">
      <div className="table-header row">
        <div className="col sortable" onClick={() => requestSort('bookingId')}>
          Booking ID <span className="sort-icon">{sortConfig?.key === 'bookingId' && (sortConfig.direction === 'ascending' ? '↑' : '↓')}</span>
        </div>
        <div className="col sortable" onClick={() => requestSort('weekCommencing')}>
          W/C <span className="sort-icon">{sortConfig?.key === 'weekCommencing' && (sortConfig.direction === 'ascending' ? '↑' : '↓')}</span>
        </div>
        <div className="col sortable" onClick={() => requestSort('candidateName')}>
          Candidate Name <span className="sort-icon">{sortConfig?.key === 'candidateName' && (sortConfig.direction === 'ascending' ? '↑' : '↓')}</span>
        </div>
        <div className="col sortable" onClick={() => requestSort('poNumber')}>
          PO Code <span className="sort-icon">{sortConfig?.key === 'poNumber' && (sortConfig.direction === 'ascending' ? '↑' : '↓')}</span>
        </div>
        <div className="col sortable" onClick={() => requestSort('placementInfo')}>
          Placement Info <span className="sort-icon">{sortConfig?.key === 'placementInfo' && (sortConfig.direction === 'ascending' ? '↑' : '↓')}</span>
        </div>
        <div className="col sortable" onClick={() => requestSort('status')}>
          Status <span className="sort-icon">{sortConfig?.key === 'status' && (sortConfig.direction === 'ascending' ? '↑' : '↓')}</span>
        </div>
      </div>
      <div className="rows">
        {sortedData.map((sheet, index) => (
          <div
            className="row"
            key={index}
            onClick={() => handleRowClick(sheet.bookingId)}
          >
            <div className="col">{sheet.bookingId}</div>
            <div className="col">{formatDate(sheet.weekCommencing)}</div>
            <div className="col">{sheet.candidateName}</div>
            <div className="col">{sheet.poNumber}</div>
            <div className="col">{sheet.placementInfo}</div>
            <div className={`col ${sheet.status}`}>
              <span className={`status ${sheet.status}`}>
                {sheet.status}
              </span>
            </div>
          </div>
        ))}
      </div>
      <ConfirmPopup
        bookingId={selectedBookingId ?? null}
        isOpen={isConfirmPopupOpen}
        isConfirming={isConfirming}
        setIsConfirming={setIsConfirming}
        onClose={() => {
          setIsConfirmPopupOpen(false);
          setSelectedBookingId(null);
          setError(null);
        }}
        onConfirm={handleConfirmSuccess}
        onDispute={() => {
          setIsConfirmPopupOpen(false);
          setIsRejectPopupOpen(true);
          setError(null);
        }}
        error={error}
      />
      <FeedbackPopup
        bookingId={selectedBookingId ? selectedBookingId.toString() : null}
        isOpen={isFeedbackPopupOpen}
        onClose={closeFeedbackPopup}
        candidateName={selectedTimeSheet?.candidateName || ''}
      />
      <RejectPopup
        isOpen={isRejectPopupOpen}
        onClose={() => setIsRejectPopupOpen(false)}
        onReject={handleReject}
        bookingId={selectedBookingId ? selectedBookingId.toString() : null}
      />
    </div>
  );
};